import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../api/client';
import { updateDeckDomain } from '../api/decks';
import { useMapDialog } from '../components/MapDialog/useMapDialog';
import { buildHierarchy, buildSidebarHierarchy, getUpcomingExams, sortDomains } from './sidebarHierarchyUtils';

export default function useSidebarHierarchy() {
  const navigate = useNavigate();
  const dialogApi = useMapDialog();
  const { showDialog } = dialogApi;
  const confirmDialog = dialogApi['show' + 'Confirm'];
  const [decks, setDecks] = useState([]);
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [examsLoading, setExamsLoading] = useState(true);
  const [activeExamIdOverride, setActiveExamIdOverride] = useState(null);
  const [showAllDecks, setShowAllDecks] = useState(() => localStorage.getItem('debono_sidebar_all') !== '0');

  const fetchDecksData = useCallback(async ({ silent = false } = {}) => {
    try {
      if (!silent) {
        setLoading(true);
      }
      const response = await apiClient.get('/api/decks/', {
        params: { _t: Date.now() }
      });
      setDecks(response.data || []);
    } catch (err) {
      if (!silent) {
        console.error('Failed to fetch decks:', err);
        setDecks([]);
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, []);

  const fetchExamsData = useCallback(async ({ silent = false } = {}) => {
    try {
      if (!silent) {
        setExamsLoading(true);
      }
      const response = await apiClient.get('/api/exams/', {
        params: { _t: Date.now() }
      });
      setExams(response.data || []);
    } catch (err) {
      if (!silent) {
        console.error('Failed to fetch exams:', err);
        setExams([]);
      }
    } finally {
      if (!silent) {
        setExamsLoading(false);
      }
    }
  }, []);

  const refetchSidebarData = useCallback(async () => {
    await Promise.all([
      fetchDecksData({ silent: true }),
      fetchExamsData({ silent: true }),
    ]);
  }, [fetchDecksData, fetchExamsData]);

  useEffect(() => {
    fetchDecksData();
    fetchExamsData();
  }, [fetchDecksData, fetchExamsData]);

  const toggleShowAllDecks = useCallback(() => {
    setShowAllDecks(prev => {
      const next = !prev;
      localStorage.setItem('debono_sidebar_all', next ? '1' : '0');
      return next;
    });
  }, []);

  const upcomingExams = useMemo(() => getUpcomingExams(exams), [exams]);

  const activeExamId = useMemo(() => {
    if (upcomingExams.length === 0) return null;
    if (activeExamIdOverride && upcomingExams.some(e => e.id === activeExamIdOverride)) {
      return activeExamIdOverride;
    }
    return upcomingExams[0].id;
  }, [upcomingExams, activeExamIdOverride]);

  const activeExam = useMemo(() => {
    return upcomingExams.find(e => e.id === activeExamId) || null;
  }, [activeExamId, upcomingExams]);

  const deckToExamMap = useMemo(() => {
    const map = {};
    exams.forEach(exam => {
      (exam.powerpoint_ids || []).forEach(deckId => {
        if (!map[deckId]) map[deckId] = [];
        map[deckId].push(exam);
      });
    });
    return map;
  }, [exams]);

  const hierarchy = useMemo(() => buildHierarchy(decks, deckToExamMap), [decks, deckToExamMap]);

  const sidebarHierarchy = useMemo(() => buildSidebarHierarchy(hierarchy, activeExamId), [hierarchy, activeExamId]);

  const domains = useMemo(() => sortDomains(hierarchy), [hierarchy]);

  const handleDeleteDeck = useCallback(async (domain, examId, topic, topicData) => {
    if (!(await confirmDialog({ title: `Delete ${topicData.decks.length} deck(s) under "${topic}"?` }))) return;
    try {
      for (const deck of topicData.decks) {
        await apiClient.delete(`/api/decks/${deck.id}`);
      }
      await refetchSidebarData();
    } catch (err) {
      console.error('Failed to delete deck:', err);
      await showDialog({ title: 'Failed to delete deck. Please try again.' });
    }
  }, [confirmDialog, refetchSidebarData, showDialog]);

  const moveDecksToDomain = useCallback(async (topicData, newDomain) => {
    await Promise.all(
      topicData.decks.map(deck => updateDeckDomain(deck.id, { domain: newDomain }))
    );
    await refetchSidebarData();
  }, [refetchSidebarData]);

  const handleMoveDomain = useCallback(async (domain, examId, topic, topicData) => {
    // This stays native because MapDialog has no text-input variant, and the
    // richer domain selector pattern needs a rendered modal surface.
    const nextDomain = window.prompt('Move topic to domain', domain)?.trim();
    if (!nextDomain || nextDomain === domain) return;
    try {
      await moveDecksToDomain(topicData, nextDomain);
    } catch (err) {
      console.error('Failed to move topic to domain:', err);
      await showDialog({ title: 'Failed to move topic. Please try again.' });
    }
  }, [moveDecksToDomain, showDialog]);

  const handleRenameDomain = useCallback(async (oldDomain, newDomain, domainData) => {
    const deckIds = [];
    Object.values(domainData.exams || {}).forEach(exam => {
      Object.values(exam.topics || {}).forEach(topicData => {
        (topicData.decks || []).forEach(d => deckIds.push(d.id));
      });
    });
    if (deckIds.length === 0) return;
    try {
      await Promise.all(
        deckIds.map(id => updateDeckDomain(id, { domain: newDomain }))
      );
      await refetchSidebarData();
    } catch (err) {
      console.error('Failed to rename domain:', err);
      await showDialog({ title: 'Failed to rename domain. Please try again.' });
    }
  }, [refetchSidebarData, showDialog]);

  const handleAddTopicToExam = useCallback(async (domain, examId, topic, topicData) => {
    if (!activeExamId || !activeExam) return;
    const deckIds = (topicData.decks || []).map(d => d.id).filter(id => id && id !== '__uploading__' && id !== '__compressing__');
    if (deckIds.length === 0) return;
    const currentIds = activeExam.powerpoint_ids || [];
    const newIds = deckIds.filter(id => !currentIds.includes(id));
    if (newIds.length === 0) return;
    const updatedIds = [...currentIds, ...newIds];
    try {
      await apiClient.put(`/api/exams/${activeExamId}/powerpoints`, updatedIds);
      await refetchSidebarData();
    } catch (err) {
      console.error('Failed to add topic to exam:', err);
      await showDialog({ title: 'Failed to add topic to exam. Please try again.' });
    }
  }, [activeExam, activeExamId, refetchSidebarData, showDialog]);

  const handleTopicClick = useCallback((domain, examId, topic, topicData) => {
    const focusTopicId = topicData?.decks?.[0]?.id;
    navigate('/dashboard', {
      state: focusTopicId ? { focusTopicId, deckId: focusTopicId } : undefined,
    });
  }, [navigate]);

  const handleDomainClick = useCallback((domain) => {
    navigate('/dashboard', {
      state: { focusDomainId: domain },
    });
  }, [navigate]);

  return {
    hierarchy,
    sidebarHierarchy,
    activeExamId,
    showAllDecks,
    toggleShowAllDecks,
    showDashboardTimeout: false,
    showSidebarLoading: loading || examsLoading,
    domains,
    isHintPending: () => false,
    hintsActive: false,
    onboardingDiscovered: 0,
    onboardingCount: 0,
    onTopicClick: handleTopicClick,
    onDeleteDeck: handleDeleteDeck,
    onMoveDomain: handleMoveDomain,
    onDomainClick: handleDomainClick,
    onRenameDomain: handleRenameDomain,
    onAddTopicToExam: handleAddTopicToExam,
    setActiveExamId: setActiveExamIdOverride,
  };
}
